import { APIConfiguration } from '@savgroup-front-common/configuration';
import { isFileType } from '@savgroup-front-common/core/helpers/AdditionalInformations/isFileType';
import {
  AdditionalInformation,
  AdditionalInformationExtended,
  Reason,
} from '@/types';

interface ReasonAdapterReturnValues extends Reason {
  neededInformation: AdditionalInformationExtended[];
}

interface ReasonAdapterArgs {
  reasonSelected: Reason;
  claimIds: string[];
  currentClaimId: string;
}

export const buildFileUploadEndpoints = ({
  claimId,
  id,
}: {
  claimId: string;
  id: string;
}): Record<string, { uploadEndpoint: string; deleteEndpoint: string }> => {
  return {
    [claimId]: {
      uploadEndpoint: `${APIConfiguration.claim}claims/${claimId}/additionalInformation/${id}/file`,
      deleteEndpoint: `${APIConfiguration.claim}claims/${claimId}/additionalInformation/${id}/file`,
    },
  };
};

export const reasonAdapter = ({
  reasonSelected,
  currentClaimId,
}: ReasonAdapterArgs): ReasonAdapterReturnValues => {
  const newNeededInformation: AdditionalInformationExtended[] | any[] =
    reasonSelected.neededInformation.reduce(
      (acc: AdditionalInformation[], curr: AdditionalInformation) => {
        const { id } = curr;

        return [
          ...acc,
          {
            ...curr,
            internalId: `${id}_${currentClaimId}`,
            currentClaimId,
            fileUploadEndpoints: isFileType(curr)
              ? buildFileUploadEndpoints({
                  claimId: currentClaimId,
                  id,
                })
              : null,
          },
        ];
      },
      [],
    );

  return {
    ...reasonSelected,
    neededInformation: newNeededInformation,
  };
};

export const prepareReasonAdditionalInformation = (
  claimId: string,
  reasonAdditionalInformation?: Record<string, { value: string } | string>,
  neededInformations?: AdditionalInformation[],
): { id: string; value: string }[] => {
  const neededInformationIds =
    neededInformations?.map((neededInformation) => neededInformation.id) ?? [];

  if (!reasonAdditionalInformation) {
    return [];
  }

  return Object.keys(reasonAdditionalInformation)
    .filter((key) => {
      const needInformationKey = key.endsWith(claimId)
        ? key.replace(`_${claimId}`, '')
        : key;

      return neededInformationIds.includes(needInformationKey);
    })
    .map((key) => {
      const needInformationKey = key.endsWith(claimId)
        ? key.replace(`_${claimId}`, '')
        : key;

      const neededInformation = neededInformations?.find(
        (info) => info.id === needInformationKey,
      );

      if (isFileType(neededInformation)) {
        // On ne peut pas passer un File ou un File[] dans `value` :
        // le payload est sérialisé en JSON et le backend crash. Les
        // fichiers sont uploadés à part via leur endpoint dédié.
        return { id: needInformationKey, value: '' };
      }

      const item = reasonAdditionalInformation[key];
      const value =
        typeof item === 'object' && 'value' in item ? item.value : item;

      return { id: needInformationKey, value };
    });
};
